import { useQuery } from '@tanstack/react-query';
import { FileDown, FileText, History, Link2, Printer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { TocEntry, UserSummary } from '@imsenta/shared';
import { PageHeader } from '@/components/AppShell';
import { Badge, Button, Card, CardHeader, EmptyState, Spinner, useToast } from '@/components/ui';
import { api, ApiError, downloadFile } from '@/lib/api';
import { usePortal } from '@/lib/portal';
import { fmtDate } from '@/lib/format';

interface CatalogView {
  document: {
    id: string;
    reference: string;
    slug: string;
    title: string;
    summary: string | null;
    documentTypeKey: string;
    documentTypeName: string;
    organisation: string;
    owner: UserSummary | null;
  };
  version: {
    id: string;
    label: string;
    state: string;
    publishedAt: string | null;
    effectiveFrom: string | null;
    reviewDue: string | null;
    changeSummary: string | null;
    wordCount: number;
  };
  metadata: Record<string, unknown>;
  toc: TocEntry[];
  html: string;
  relatedDocuments: { documentId: string | null; reference: string | null; label: string }[];
  referencedBy: { documentId: string; reference: string; title: string }[];
  history: { id: string; label: string; publishedAt: string | null; changeSummary: string | null }[];
}

export function CatalogDocumentPage() {
  const { key = '' } = useParams();
  const navigate = useNavigate();
  const { push } = useToast();
  const { isPortal } = usePortal();
  const catalogPath = isPortal ? '/' : '/catalog';
  const readerPath = (reference: string) => (isPortal ? `/${reference}` : `/catalog/${reference}`);
  const [activeAnchor, setActiveAnchor] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['catalog-doc', key],
    queryFn: () => api.get<CatalogView>(`/catalog/${key}`),
  });

  // Highlight the section currently in view.
  useEffect(() => {
    if (!data) return;
    const headings = Array.from(document.querySelectorAll<HTMLElement>('.pf-prose section.doc-section'));
    if (!headings.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveAnchor(visible[0].target.id);
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 },
    );
    headings.forEach((heading) => observer.observe(heading));
    return () => observer.disconnect();
  }, [data]);

  if (isLoading) return <Spinner label="Opening document…" />;

  if (error || !data) {
    return (
      <Card>
        <EmptyState
          icon={<FileText className="size-10" />}
          title="Not available in the catalog"
          description={error instanceof ApiError ? error.message : 'This document may not be published yet.'}
          action={<Button onClick={() => navigate(catalogPath)}>Back to the catalog</Button>}
        />
      </Card>
    );
  }

  const { document: doc, version, metadata } = data;
  const tags = Array.isArray(metadata.tags) ? (metadata.tags as string[]) : [];

  const download = async () => {
    setDownloading(true);
    try {
      await downloadFile(`/documents/${doc.id}/export`, { format: 'pdf', versionId: version.id });
      push('PDF downloaded', 'success');
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'Download failed', 'danger');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <PageHeader
        back={{ to: catalogPath, label: 'All policies' }}
        title={doc.title}
        description={doc.summary ?? undefined}
        meta={
          <>
            <Badge tone="neutral">{doc.reference}</Badge>
            <Badge tone="brand">{doc.documentTypeName}</Badge>
            <Badge tone="success">{version.label}</Badge>
            {String(metadata.classification ?? '') ? <Badge tone="warning">{String(metadata.classification)}</Badge> : null}
            <span className="text-[12.5px] text-ink-500">Issued {fmtDate(version.publishedAt)}</span>
          </>
        }
        actions={
          <>
            <Button icon={<Printer className="size-4" />} onClick={() => window.print()}>
              Print
            </Button>
            <Button variant="primary" icon={<FileDown className="size-4" />} loading={downloading} onClick={download}>
              Download PDF
            </Button>
          </>
        }
      />

      <div className="grid gap-5 lg:grid-cols-[240px_minmax(0,1fr)] [&>*]:min-w-0">
        <aside className="pf-no-print space-y-4 lg:sticky lg:top-20 lg:self-start">
          {data.toc.length ? (
            <Card className="max-h-[60vh] overflow-y-auto">
              <p className="px-4 pt-3.5 pb-2 text-[11px] font-semibold tracking-wider text-ink-400 uppercase">Contents</p>
              <nav className="pb-3">
                {data.toc.map((entry) => (
                  <a
                    key={entry.anchor}
                    href={`#${entry.anchor}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(entry.anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      setActiveAnchor(entry.anchor);
                    }}
                    className={`block border-l-2 py-1 pr-3 text-[12.5px] transition-colors ${
                      activeAnchor === entry.anchor
                        ? 'border-brand-500 bg-brand-50/60 font-medium text-brand-700'
                        : 'border-transparent text-ink-600 hover:border-ink-300 hover:text-ink-900'
                    }`}
                    style={{ paddingLeft: `${(entry.depth - 1) * 12 + 16}px` }}
                  >
                    {entry.number ? <span className="mr-1.5 text-ink-400">{entry.number}</span> : null}
                    {entry.title}
                  </a>
                ))}
              </nav>
            </Card>
          ) : null}

          <Card>
            <CardHeader title="Document details" />
            <dl className="space-y-2 px-4 py-3.5 text-[12.5px]">
              <Row label="Owner">{doc.owner?.name ?? String(metadata.owner ?? '—')}</Row>
              <Row label="Approver">{String(metadata.approver ?? '—')}</Row>
              <Row label="Effective">{fmtDate(version.effectiveFrom)}</Row>
              <Row label="Next review">{fmtDate(version.reviewDue)}</Row>
              <Row label="Words">{version.wordCount}</Row>
            </dl>
            {tags.length ? (
              <div className="flex flex-wrap gap-1 border-t border-ink-100 px-4 py-3">
                {tags.map((tag) => (
                  <Link key={tag} to={`${catalogPath}?tag=${encodeURIComponent(tag)}`} className="rounded bg-ink-100 px-1.5 py-0.5 text-[11px] text-ink-600 hover:bg-ink-200">
                    {tag}
                  </Link>
                ))}
              </div>
            ) : null}
          </Card>

          {data.relatedDocuments.length ? (
            <Card>
              <CardHeader title="Related documents" />
              <ul className="divide-y divide-ink-100">
                {data.relatedDocuments.map((ref, index) => (
                  <li key={index} className="px-4 py-2 text-[12.5px]">
                    <Link to={readerPath(String(ref.reference ?? ref.documentId))} className="inline-flex items-center gap-1.5 text-brand-700 hover:underline">
                      <Link2 className="size-3" />
                      {ref.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          {data.referencedBy.length ? (
            <Card>
              <CardHeader title="Referenced by" />
              <ul className="divide-y divide-ink-100">
                {data.referencedBy.map((ref) => (
                  <li key={ref.documentId} className="px-4 py-2 text-[12.5px]">
                    <Link to={readerPath(ref.reference)} className="text-brand-700 hover:underline">
                      {ref.reference} · {ref.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </aside>

        <div>
          <Card className="px-5 py-6 sm:px-8 sm:py-8 lg:px-12">
            <header className="mb-8 border-b-2 border-brand-500 pb-5">
              <p className="text-[11.5px] font-semibold tracking-wider text-ink-400 uppercase">{doc.organisation}</p>
              <h1 className="mt-1.5 text-2xl leading-tight font-semibold text-ink-900 sm:text-3xl">{doc.title}</h1>
              <p className="mt-2 text-[13px] text-ink-500">
                {doc.reference} · {version.label} · {doc.documentTypeName}
                {version.effectiveFrom ? ` · effective ${fmtDate(version.effectiveFrom)}` : ''}
              </p>
            </header>

            {/* The server renders and escapes this HTML from the stored structured content. */}
            <div className="pf-prose" dangerouslySetInnerHTML={{ __html: data.html }} />
          </Card>

          {data.history.length > 1 ? (
            <Card className="pf-no-print mt-5">
              <CardHeader title="Version history" description="Previous issues of this document." />
              <ul className="divide-y divide-ink-100">
                {data.history.map((entry) => (
                  <li key={entry.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-5 py-2.5 text-[13px]">
                    <History className="size-3.5 text-ink-300" />
                    <Badge tone={entry.id === version.id ? 'success' : 'neutral'}>{entry.label}</Badge>
                    <span className="min-w-0 flex-1 basis-40 truncate text-ink-600">{entry.changeSummary ?? '—'}</span>
                    <span className="text-[12px] text-ink-400">{fmtDate(entry.publishedAt)}</span>
                    <Button size="sm" onClick={() => downloadFile(`/documents/${doc.id}/export`, { format: 'pdf', versionId: entry.id })}>
                      PDF
                    </Button>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </div>
      </div>
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="shrink-0 text-ink-500">{label}</dt>
      <dd className="text-right font-medium text-ink-800">{children}</dd>
    </div>
  );
}
